import PrivacyField from '@/components/PrivacyField'
import { Badge } from '@/components/ui/badge'
import ContactIcons from '@/icons/ContactIcons'
import ShuffleIcons from '@/icons/ShuffleIcons'
import {
    CONTACT_ROLE_OPTIONS,
    CUSTOMER_SOURCES,
    INDUSTRIES,
} from '@/modules/Customer/constants'
import { createCustomerLogs } from '@/modules/Customer/services'
import { AlertTriangle, Edit, Star } from 'lucide-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Typogaphy } from 'vnetwork-ui'
import { yupResolver } from '@hookform/resolvers/yup'

import ContactActionsMenu from './contact-actions-menu'
import ContactEditForm from './contact-edit-form'
import CreateAccountDialog from './create-account-dialog'
import type { CreateAccountForm, ViewEditContactProps } from './types'
import { createAccountSchema } from './types'
import { useContactActions } from './use-contact-actions'
import { useCustomerReadOnly } from '../../../CustomerReadOnlyContext'

const FieldItem = ({
    label,
    value,
    className = '',
}: {
    label: string
    value: React.ReactNode
    className?: string
}) => (
    <div className={`flex flex-col space-y-1 ${className}`}>
        <Typogaphy className="text-sm font-medium text-gray-600">
            {label}
        </Typogaphy>
        <Typogaphy className="text-sm text-gray-900">{value}</Typogaphy>
    </div>
)

const ViewEditContact: React.FC<ViewEditContactProps> = ({
    data,
    isEditing,
    setIsEditing,
    editFormControl,
    onSave,
    isPending,
    customerId,
}) => {
    const { t } = useTranslation()
    const { t: tCustomer } = useTranslation('customer')
    const contactRoleOptions = CONTACT_ROLE_OPTIONS(tCustomer)
    const isReadOnly = useCustomerReadOnly()
    const [showCreateAccountDialog, setShowCreateAccountDialog] =
        useState(false)

    const createAccountForm = useForm<CreateAccountForm>({
        defaultValues: {
            password: '',
            password_confirmation: '',
            send_welcome_email: true,
        },
        resolver: yupResolver(createAccountSchema),
    })

    const contactId = data?.customer_contact_id

    const {
        canImpersonate,
        hasKeycloakAccount,
        hasNoAccount,
        isAccountEnabled,
        handleImpersonate,
        isImpersonating,
        handleDisable,
        isDisabling,
        handleEnable,
        isEnabling,
        handleSync,
        isSyncing,
        handleUnlink,
        isUnlinking,
        handleSetPrimary,
        isSettingPrimary,
        handleCreateAccount,
        isCreatingAccount,
    } = useContactActions(contactId, customerId, {
        onCreateAccountSuccess: () => {
            setShowCreateAccountDialog(false)
            createAccountForm.reset()
        },
    })

    if (isEditing) {
        return (
            <ContactEditForm
                editFormControl={editFormControl}
                data={data}
                onSave={onSave}
                isPending={isPending}
                onCancel={() => {
                    setIsEditing(false)
                    editFormControl.reset(data)
                }}
            />
        )
    }

    // View Mode
    return (
        <div className="mt-4 bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <ContactIcons />
                    <Typogaphy className="font-medium">
                        {t('contact-information')}
                    </Typogaphy>
                    {data?.contact_firstable && (
                        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 flex items-center gap-1">
                            <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                            {tCustomer('contact.primary-contact')}
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {!isReadOnly && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setIsEditing(true)}
                        >
                            <Edit className="h-4 w-4 mr-1" />
                            {t('edit')}
                        </Button>
                    )}
                    {canImpersonate && !isReadOnly && (
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleImpersonate()}
                            disabled={isImpersonating}
                        >
                            <ShuffleIcons className="h-4 w-4 mr-1" />
                            {t('impersonate')}
                        </Button>
                    )}
                    <ContactActionsMenu
                        hasKeycloakAccount={hasKeycloakAccount}
                        hasNoAccount={hasNoAccount}
                        isAccountEnabled={isAccountEnabled}
                        isContactPrimary={!!data?.contact_firstable}
                        onDisable={() => handleDisable()}
                        isDisabling={isDisabling}
                        onEnable={() => handleEnable()}
                        isEnabling={isEnabling}
                        onUnlink={() => handleUnlink()}
                        isUnlinking={isUnlinking}
                        onSync={() => handleSync()}
                        isSyncing={isSyncing}
                        onSetPrimary={() => handleSetPrimary()}
                        isSettingPrimary={isSettingPrimary}
                        onCreateAccount={() => setShowCreateAccountDialog(true)}
                    />
                </div>
            </div>

            {/* Alert for disabled account */}
            {hasKeycloakAccount && !isAccountEnabled && (
                <div className="mb-4 flex items-center gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0" />
                    <span className="text-sm text-orange-700">
                        {tCustomer('contact.account-disabled-warning')}
                    </span>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                <FieldItem
                    label={t('contact_name')}
                    value={data?.contact_name}
                />
                <FieldItem
                    label="Email"
                    value={
                        <PrivacyField
                            onReveal={() => {
                                createCustomerLogs({
                                    customerID: customerId,
                                    content: 'email',
                                })
                            }}
                            className="h-[18px]"
                            value={data?.contact_email || ''}
                            type="email"
                        />
                    }
                />
                <FieldItem
                    label={t('employee_phone_number')}
                    value={
                        <PrivacyField
                            onReveal={() => {
                                createCustomerLogs({
                                    customerID: customerId,
                                    content: 'phone',
                                })
                            }}
                            className="h-[18px] min-h-[18px]"
                            value={data?.contact_phone || '-'}
                            type="phone"
                        />
                    }
                />
                <FieldItem
                    label={t('position_id')}
                    value={data?.contact_position || '-'}
                />
                <FieldItem
                    label={t('customer_industry')}
                    value={
                        INDUSTRIES.find(
                            (item) => item.value === data?.contact_industry
                        )?.label || '-'
                    }
                />
                <FieldItem
                    label={t('customer_source')}
                    value={
                        CUSTOMER_SOURCES(t).find(
                            (item) => item.value === data?.contact_source
                        )?.label || '-'
                    }
                />
                <FieldItem
                    label={tCustomer('contact_roles.label')}
                    value={
                        <div className="flex flex-wrap gap-1">
                            {(data?.contact_roles?.length
                                ? data.contact_roles
                                : ['customer']
                            ).map((role) => (
                                <Badge
                                    key={role}
                                    variant="outline-primary"
                                    className="text-xs"
                                >
                                    {contactRoleOptions.find(
                                        (item) => item.value === role
                                    )?.label || role}
                                </Badge>
                            ))}
                        </div>
                    }
                />
                <FieldItem
                    label={t('customer_language')}
                    value={
                        <div className="flex items-center gap-2">
                            <Badge
                                variant={
                                    data?.customer_language !== 'vi'
                                        ? 'default'
                                        : 'outline'
                                }
                                className={`uppercase ${
                                    data?.customer_language !== 'vi'
                                        ? 'bg-orange-500 hover:bg-orange-600'
                                        : ''
                                }`}
                            >
                                {data?.customer_language || 'vi'}
                            </Badge>
                            {data?.customer_language !== 'vi' && (
                                <Typogaphy className="text-xs text-orange-600 font-medium">
                                    {t('non-vietnamese-language')}
                                </Typogaphy>
                            )}
                        </div>
                    }
                />
                <FieldItem
                    label={t('customer_service_interestings')}
                    value={
                        data?.contact_service_interestings?.join(', ') || '-'
                    }
                    className="line-clamp-1"
                />
                <FieldItem
                    label={t('customer_cs_followers')}
                    value={
                        data?.contact_cs_followers_detail
                            ?.map((i) => i.employee_name)
                            ?.join(', ') || '-'
                    }
                    className="line-clamp-1"
                />
                <FieldItem
                    label={t('customer_sales_followers')}
                    value={
                        data?.contact_sale_followers_detail
                            ?.map((i) => i.employee_name)
                            ?.join(', ') || '-'
                    }
                    className="line-clamp-1"
                />
                <FieldItem
                    label={t('website')}
                    value={data?.customer_website || '-'}
                />
                <FieldItem
                    label={t('address')}
                    value={data?.contact_address || '-'}
                />
                <FieldItem
                    label={t('note')}
                    value={data?.contact_note || '-'}
                    className="col-span-full"
                />
            </div>

            {/* Create Account Dialog */}
            <CreateAccountDialog
                open={showCreateAccountDialog}
                onOpenChange={setShowCreateAccountDialog}
                form={createAccountForm}
                contactEmail={data?.contact_email}
                onSubmit={(formData) => handleCreateAccount(formData)}
                isCreating={isCreatingAccount}
            />
        </div>
    )
}

export default ViewEditContact
